import React, { useEffect, useState, useCallback } from 'react';
import { MapPin, Plus, RefreshCw } from 'lucide-react';

const GlassPanel: React.FC<{ children: React.ReactNode; className?: string; title?: string; icon?: any; headerAction?: React.ReactNode }> = ({ children, className = '', title, icon: Icon, headerAction }) => (
  <div className={`bg-white/5 backdrop-blur-2xl border border-white/10 rounded-2xl overflow-hidden shadow-2xl ${className}`}>
    {(title || Icon || headerAction) && (
      <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between bg-white/[0.02]">
        <div className="flex items-center gap-3">
          {Icon && <Icon className="w-5 h-5 text-blue-400" />}
          {title && <h3 className="text-sm font-bold tracking-tight text-blue-100/90 uppercase">{title}</h3>}
        </div>
        {headerAction}
      </div>
    )}
    <div className="p-6">{children}</div>
  </div>
);

interface Location {
  id?: string;
  _id?: string;
  name: string;
}

export function Locations() {
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [adding, setAdding] = useState(false);

  const fetchLocations = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('csense_auth_token');
      const response = await fetch('/api/locations', { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) {
        const data = await response.json();
        setLocations(data.locations || []);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLocations(); }, [fetchLocations]);

  const addLocation = async () => {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      const token = localStorage.getItem('csense_auth_token');
      const response = await fetch('/api/locations', {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName.trim() })
      });
      if (response.ok) {
        setNewName('');
        fetchLocations();
      }
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0b14] text-white p-6 space-y-8 pb-32">
      <div>
        <h1 className="text-3xl font-black tracking-tighter uppercase flex items-center gap-3"><MapPin className="w-8 h-8 text-blue-400" /> Locations</h1>
        <p className="text-sm text-blue-100/40 mt-1 font-medium tracking-wide">Sites and zones your cameras and devices are deployed across.</p>
      </div>

      <GlassPanel title="Add Location" icon={Plus}>
        <div className="flex gap-3">
          <input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addLocation()}
            placeholder="e.g. Main Gate, Warehouse B..."
            className="flex-1 bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-white placeholder:text-blue-100/20 outline-none focus:border-blue-500/40"
          />
          <button onClick={addLocation} disabled={adding || !newName.trim()} className="px-6 py-3 bg-blue-600 hover:bg-blue-500 disabled:opacity-40 rounded-xl font-bold text-sm transition-all">
            {adding ? 'Adding...' : 'Add'}
          </button>
        </div>
      </GlassPanel>

      <GlassPanel title="All Locations" icon={MapPin} headerAction={
        <button onClick={fetchLocations} className="text-blue-100/40 hover:text-white"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></button>
      }>
        {loading ? (
          <div className="py-12 text-center text-blue-100/20 text-sm uppercase tracking-widest">Loading...</div>
        ) : locations.length === 0 ? (
          <div className="py-12 text-center text-blue-100/20 text-sm uppercase tracking-widest">No locations added yet.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {locations.map((loc, idx) => (
              <div key={loc.id || loc._id || idx} className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center gap-3">
                <MapPin className="w-4 h-4 text-blue-400 flex-shrink-0" />
                <span className="text-sm font-bold text-white truncate">{loc.name}</span>
              </div>
            ))}
          </div>
        )}
      </GlassPanel>
    </div>
  );
}
